import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X, Sparkles, ChevronRight, Eye, Link2, AlertTriangle, Clock, Unlink, Copy, Radio, BookOpen, TrendingUp } from 'lucide-react';
import type { InsightKind, InsightRecord, InsightStatus } from '@cortex/shared';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';
import { useSessionContext } from '../../hooks/sessionContext';
import { qk } from '../../hooks/useVault';
import { AgentMarkdown } from '../agent/AgentMarkdown';

const KIND_META: Record<InsightKind, { label: string; icon: React.ReactNode; cls: string }> = {
  LINK_SUGGESTION: { label: 'Link', icon: <Link2 size={12} />, cls: 'teal' },
  CONTRADICTION: { label: 'Contradiction', icon: <AlertTriangle size={12} />, cls: 'crit' },
  STALE_NOTE: { label: 'Stale', icon: <Clock size={12} />, cls: 'warn' },
  ORPHAN: { label: 'Orphan', icon: <Unlink size={12} />, cls: '' },
  DUPLICATE: { label: 'Duplicate', icon: <Copy size={12} />, cls: 'warn' },
  MONITOR_ALERT: { label: 'Watcher', icon: <Radio size={12} />, cls: 'crit' },
  RESEARCH_BRIEF: { label: 'Brief', icon: <BookOpen size={12} />, cls: 'teal' },
  PATTERN: { label: 'Pattern', icon: <TrendingUp size={12} />, cls: 'accent' },
};

export function useInsightCounts() {
  return useQuery({ queryKey: ['insights', 'counts'], queryFn: Api.insights.counts, refetchInterval: 60_000, staleTime: 30_000 });
}

export function InsightsView() {
  const [status, setStatus] = useState<InsightStatus>('NEW');
  const [kind, setKind] = useState<InsightKind | ''>('');
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['insights', status, kind], queryFn: () => Api.insights.list({ status, kind: kind || undefined }), staleTime: 10_000, refetchInterval: 30_000 });
  const counts = q.data?.counts.byKind ?? {};
  return (
    <div className="insights-view">
      <div className="insights-head">
        <div className="modes">
          {(['NEW', 'ACCEPTED', 'DISMISSED', 'SUPERSEDED'] as InsightStatus[]).map((s) => (
            <button key={s} className={status === s ? 'on' : ''} onClick={() => setStatus(s)}>{s.toLowerCase()}</button>
          ))}
        </div>
        <div className="kind-chips">
          <button className={`chip${kind === '' ? ' teal' : ''}`} onClick={() => setKind('')}>all</button>
          {(Object.keys(KIND_META) as InsightKind[]).map((k) => (
            <button key={k} className={`chip${kind === k ? ' teal' : ''}`} onClick={() => setKind(k)}>{KIND_META[k].icon} {KIND_META[k].label}{counts[k] ? ` ${counts[k]}` : ''}</button>
          ))}
        </div>
        <span className="spacer" />
        <span className="faint" style={{ fontSize: 11 }}>Nothing here is auto-applied. Dismissed findings never return.</span>
      </div>
      <div className="insights-list">
        {q.data?.insights.length === 0 && (
          <div className="agent-empty">
            <Sparkles size={18} />
            <div>{status === 'NEW' ? 'No new insights. The workers run nightly; an ADMIN can run one now from Admin → Jobs.' : `No ${status.toLowerCase()} insights.`}</div>
          </div>
        )}
        {q.data?.insights.map((i) => <InsightRow key={i.id} i={i} open={open === i.id} onToggle={() => setOpen(open === i.id ? null : i.id)} />)}
      </div>
    </div>
  );
}

function InsightRow({ i, open, onToggle }: { i: InsightRecord; open: boolean; onToggle: () => void }) {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const openNote = useUi((s) => s.openNote);
  const session = useSessionContext();
  const meta = KIND_META[i.kind];
  const refresh = () => (qc.invalidateQueries({ queryKey: ['insights'] }), qc.invalidateQueries({ queryKey: ['proposals'] }));
  const accept = async () => {
    try {
      const r = await Api.insights.accept(i.id);
      toast(r.applied ? `Applied → ${r.applied.path}` : 'Accepted');
      refresh();
      if (r.applied) (qc.invalidateQueries({ queryKey: qk.note(r.applied.noteId) }), qc.invalidateQueries({ queryKey: qk.tree }));
    } catch (err) {
      toast((err as Error).message, 'crit');
    }
  };
  const dismiss = async () => {
    await Api.insights.dismiss(i.id);
    refresh();
  };
  return (
    <div className={`insight ${i.status.toLowerCase()}`}>
      <button className="insight-head" onClick={onToggle}>
        <ChevronRight size={12} className={`chev${open ? ' open' : ''}`} />
        <span className={`chip ${meta.cls}`} style={{ fontSize: 9, minWidth: 86, justifyContent: 'center' }}>{meta.icon} {meta.label}</span>
        <span className="t">{i.title}</span>
        <span className="conf mono" title="confidence">{Math.round(i.confidence * 100)}%</span>
        <span className="faint mono" style={{ fontSize: 10 }}>{new Date(i.createdAt).toLocaleDateString()}</span>
      </button>
      {open && (
        <div className="insight-body">
          <AgentMarkdown text={i.body} />
          {i.refs.length > 0 && (
            <div className="insight-refs">
              {i.refs.map((r, k) => (
                <button key={k} className="chip" disabled={!r.noteId} onClick={() => r.noteId && openNote(r.noteId)} title={r.quote ?? r.path ?? ''}><Eye size={10} /> {r.title}</button>
              ))}
            </div>
          )}
          {i.status === 'NEW' && (
            <div className="proposal-actions">
              {session.user?.role !== 'VIEWER' && <button className="primary" onClick={() => void accept()}><Check size={12} /> {i.proposalId ? 'Accept and apply' : 'Accept'}</button>}
              <button className="danger" onClick={() => void dismiss()}><X size={12} /> Dismiss (never show again)</button>
              {i.proposalId && <button className="ghost" onClick={() => useUi.getState().setDockTab('proposals')}>See the patch</button>}
            </div>
          )}
          <div className="faint mono" style={{ fontSize: 10 }}>{i.worker} · {i.status.toLowerCase()}{i.resolvedAt ? ` · ${new Date(i.resolvedAt).toLocaleString()}` : ''}</div>
        </div>
      )}
    </div>
  );
}
